// ============================================================================
// TENANT CONFIGURATION
// ============================================================================
// Centralized tenant branding configuration for the application.
// In the future, these values will be dynamically retrieved from the
// "tenant-hub" tenant management system via API calls.
// ============================================================================

/**
 * Tenant branding configuration interface
 */
export interface TenantBranding {
  /** Tenant/Organization name displayed on documents and reports */
  organizationName: string;
  /** Short name or abbreviation (optional) */
  organizationShortName?: string;
  /** Logo URL for reports, documents, and branding */
  logoUrl: string;
  /** Alternative text for logo accessibility */
  logoAlt: string;
  /** Primary brand color (hex) */
  primaryColor?: string;
  /** Secondary brand color (hex) */
  secondaryColor?: string;
}

/**
 * Complete tenant configuration interface
 */
export interface TenantConfig {
  /** Unique tenant identifier */
  tenantId: string;
  /** Tenant code for routing/identification */
  tenantCode: string;
  /** Branding configuration */
  branding: TenantBranding;
  /** Application/System name */
  systemName: string;
  /** Application short name */
  systemShortName: string;
}

// ============================================================================
// CURRENT TENANT CONFIGURATION
// ============================================================================
// TODO: Replace with dynamic tenant-hub API integration
// These values are currently hardcoded but will be fetched from tenant-hub
// based on the authenticated user's tenant context.
// ============================================================================

/**
 * Current tenant configuration
 * @future This will be replaced with API call to tenant-hub
 */
export const CURRENT_TENANT_CONFIG: TenantConfig = {
  tenantId: 'valuvanadu-capital',
  tenantCode: 'VCL',
  branding: {
    organizationName: 'Valuvanadu Capital Ltd',
    organizationShortName: 'VCL',
    logoUrl: '/valuvanadu_logo.png',
    logoAlt: 'Valuvanadu Capital Ltd Logo',
    primaryColor: '#1e40af',
    secondaryColor: '#64748b',
  },
  systemName: 'Digital Approval System',
  systemShortName: 'DAS',
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get the current tenant branding configuration
 * @future This will fetch from tenant-hub API or context
 */
export const getTenantBranding = (): TenantBranding => {
  return CURRENT_TENANT_CONFIG.branding;
};

/**
 * Get the current tenant configuration
 * @future This will fetch from tenant-hub API or context
 */
export const getTenantConfig = (): TenantConfig => {
  return CURRENT_TENANT_CONFIG;
};

/**
 * Get formatted display name combining system and tenant names
 * Format: "DAS | Valuvanadu Capital Ltd"
 */
export const getDisplayName = (): string => {
  const config = CURRENT_TENANT_CONFIG;
  return `${config.systemShortName} | ${config.branding.organizationName}`;
};

/**
 * Get document metadata for approval documents and reports
 * This provides consistent branding across all generated documents
 * Logo is dynamically retrieved from sessionStorage (set during login from tenant-hub)
 */
export const getDocumentMetadata = () => {
  const config = CURRENT_TENANT_CONFIG;

  // Tenant identity comes from the session established at login (written by
  // authService from the Tenant Hub response). There is deliberately NO
  // fallback to CURRENT_TENANT_CONFIG.branding: this is a multi-tenant app,
  // and defaulting to the hardcoded organisation stamps one customer's name
  // and logo onto another customer's approval certificates. An unbranded
  // tenant renders no logo and no company name instead of someone else's.
  const tenantLogoBase64 = typeof window !== 'undefined'
    ? sessionStorage.getItem('tenant_logo_base64')
    : null;

  const tenantDisplayName = typeof window !== 'undefined'
    ? sessionStorage.getItem('tenant_display_name')
    : null;

  const tenantName = typeof window !== 'undefined'
    ? sessionStorage.getItem('tenant_name')
    : null;

  // Data URL when the tenant supplied a logo; empty string otherwise. The
  // document template omits the <img> entirely when this is empty.
  const logoUrl = tenantLogoBase64 && tenantLogoBase64.trim() !== ''
    ? `data:image/png;base64,${tenantLogoBase64}`
    : '';

  return {
    companyName: (tenantDisplayName || tenantName || '').trim(),
    systemName: config.systemName,
    logoUrl,
  };
};

/**
 * Get report header configuration
 * Used for consistent branding on all reports
 * Logo is dynamically retrieved from sessionStorage (set during login from tenant-hub)
 */
export const getReportHeaderConfig = () => {
  const config = CURRENT_TENANT_CONFIG;

  // Same rule as getDocumentMetadata: tenant identity comes from the session,
  // with NO fallback to CURRENT_TENANT_CONFIG.branding. A report must never
  // carry a different customer's name or logo.
  const tenantLogoBase64 = typeof window !== 'undefined'
    ? sessionStorage.getItem('tenant_logo_base64')
    : null;

  const tenantDisplayName = typeof window !== 'undefined'
    ? sessionStorage.getItem('tenant_display_name')
    : null;

  const tenantName = typeof window !== 'undefined'
    ? sessionStorage.getItem('tenant_name')
    : null;

  const organizationName = (tenantDisplayName || tenantName || '').trim();

  const logoUrl = tenantLogoBase64 && tenantLogoBase64.trim() !== ''
    ? `data:image/png;base64,${tenantLogoBase64}`
    : '';

  return {
    organizationName,
    systemName: config.systemName,
    systemShortName: config.systemShortName,
    logoUrl,
    logoAlt: organizationName ? `${organizationName} Logo` : '',
  };
};

export default CURRENT_TENANT_CONFIG;
